import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, CircularProgress, Container, Divider,
  Fade, Grid, Grow, Paper, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useApi } from '../../hooks/useApi';
import { queryScopeFor, fleetCheckByKey } from './fleetCatalog';
import { buildManualRows } from './manualRows';
import CheckPicker from './CheckPicker';
import ResultsTable from './ResultsTable';
import FleetImage from '../../media/Lamp Poses-03-1.png';


function Stat({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Fleet Reporting -- pick checks, pick scope, get rows.
 *
 * Two row sources feed one table:
 *   automated -- cma/audit/report returns per-target rows with Knowledge
 *                directives already applied, so results agree with what a
 *                customer's Technical Health page shows for the same check and
 *                date. Paged, because a wide date range can be large.
 *   manual    -- attested values have no fact, no producer and no daily trail,
 *                so buildManualRows generates one row per account per check from
 *                the accountprofile directives. Not paged; not per day.
 *
 * No scoring, tiers or gap chips by design -- see
 * cma.wiki/Architecture/Fleet-Reporting.md.
 */
export default function FleetReport() {
  const jApi = useApi();

  const theme = useTheme();


  const [checks, setChecks] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [pickedAccounts, setPickedAccounts] = useState([]);
  const [from, setFrom] = useState(dayjs());
  const [to, setTo] = useState(dayjs());

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await jApi.request('GET', 'cma/account');
        if (cancelled) return;
        setAccountOptions(
          (res.data || [])
            .map((a) => ({ code: a.code, label: `${a.name} (${a.code})` }))
            .sort((x, y) => x.label.localeCompare(y.label)),
        );
      } catch (ex) {
        if (!cancelled) setError(`Could not load accounts: ${ex.message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scope = useMemo(() => queryScopeFor(checks), [checks]);
  const canRun = checks.length > 0 && from && to && !loading;

  const MAX_PAGES = 50;

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    const fromStr = from.format('YYYY-MM-DD');
    const toStr = to.format('YYYY-MM-DD');
    // No account picked means "every account you can see" -- the same list the
    // picker was populated from, so manual rows and the API agree on scope.
    const accountsInScope = (pickedAccounts.length ? pickedAccounts : accountOptions)
      .map((a) => a.code);

    try {
      // -- Manual attestations ------------------------------------------------
      // Generated, not queried: one row per account per check, stamped with the
      // range's end date. Fetched up front so a manual-only run can publish
      // without touching the audit store at all.
      let manualRows = [];
      if (scope.manualKeys.length) {
        const mp = new URLSearchParams({ directive_kind: 'manual' });
        if (pickedAccounts.length === 1) mp.set('account', pickedAccounts[0].code);
        const { data } = await jApi.request('GET', `cma/accountprofile?${mp.toString()}`);
        manualRows = buildManualRows({
          manualKeys: scope.manualKeys,
          accounts: accountsInScope,
          directives: Array.isArray(data) ? data : [],
          asOfDate: toStr,
          labelByKey: Object.fromEntries(
            scope.manualKeys.map((k) => [k, fleetCheckByKey[k]?.label || k]),
          ),
        });
      }

      // cma/audit/report requires auditRunIds and 400s without them, so a
      // manual-only selection must not call it at all.
      if (!scope.auditRunIds.length) {
        setResult({
          from: fromStr,
          to: toStr,
          rows: manualRows,
          rowCount: manualRows.length,
          records: 0,
          manualCount: manualRows.length,
          suppressedCount: 0,
          truncated: false,
          accounts: new Set(manualRows.map((r) => r.account)).size,
          pages: 1,
          hasMore: false,
          loadingMore: false,
          stoppedEarly: false,
        });
        return;
      }

      const params = new URLSearchParams();
      params.set('auditRunIds', scope.auditRunIds.join(','));
      params.set('checkKeys', scope.checkKeys.join(','));
      params.set('from', fromStr);
      params.set('to', toStr);
      if (pickedAccounts.length) {
        params.set('accounts', pickedAccounts.map((a) => a.code).join(','));
      }

      // A wide date range comes back across several pages. Publish after each one
      // so the table fills progressively instead of the page sitting blank until
      // the last page lands.
      let factRows = [];
      let token = null;
      let pages = 0;
      let records = 0;
      let suppressed = 0;
      let truncated = false;

      do {
        const url = token
          ? `cma/audit/report?${params.toString()}&continuationToken=${encodeURIComponent(token)}`
          : `cma/audit/report?${params.toString()}`;
        const { data } = await jApi.request('GET', url);

        // concat, not push(...spread) -- spreading 20k+ elements as arguments
        // risks blowing the call stack.
        factRows = factRows.concat(data.rows || []);
        records += data.records || 0;
        suppressed += data.suppressedCount || 0;
        truncated = truncated || !!data.truncated;
        token = data.continuationToken;
        pages += 1;

        const more = !!token && pages < MAX_PAGES;
        const rows = manualRows.concat(factRows);
        setResult({
          ...data,
          rows,
          rowCount: rows.length,
          records,
          manualCount: manualRows.length,
          suppressedCount: suppressed,
          truncated,
          accounts: new Set(rows.map((r) => r.account)).size,
          pages,
          hasMore: !!token,
          loadingMore: more,
          stoppedEarly: !!token && pages >= MAX_PAGES,
        });
      } while (token && pages < MAX_PAGES);
    } catch (ex) {
      setError(ex.response?.data?.error?.message || ex.message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };



  const rows = result?.rows || [];

    const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      opacity: 0.5,
    },
  };

  // The glass-panel treatment the cards on Operation Center / Home use.
  const panelSx = {
    background: theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.8)
      : alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    borderRadius: 3,
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  };


  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Fade in timeout={500}>
        <Box sx={{ pt: 1, pb: 2 }}>
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1,
              borderRadius: 2,
              background: alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: alpha(theme.palette.background.paper, 0.8),
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
              },
            }}
          >
            <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5,
                  borderRadius: 1.5, transition: 'all 0.2s ease',
                  '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) },
                }}
              >
                <HomeTwoToneIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 500 }}>
                  Home
                </Typography>
              </Box>
            </Link>
            <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, px: 1, py: 0.5 }}>
              Fleet Reporting
            </Typography>
          </Box>
        </Box>
      </Fade>

      <Fade in timeout={600}>
        <Box sx={{ ...headerGradient, p: 4, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                width: 80, height: 80, borderRadius: 3, overflow: 'hidden', flexShrink: 0,
                boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
              }}
            >
              <img width="100%" height="100%" src={FleetImage} alt="Logo" style={{ objectFit: 'cover' }} />
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Fleet Reporting
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Audit facts across every account, one table
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Fade>

      <Grow in timeout={800}>
        <Paper elevation={0} sx={{ ...panelSx, p: 3, mb: 3 }}>

        <Stack spacing={3}>
          <CheckPicker value={checks} onChange={setChecks} disabled={loading} />

          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                multiple
                limitTags={3}
                disabled={loading}
                options={accountOptions}
                value={pickedAccounts}
                onChange={(e, v) => setPickedAccounts(v)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Accounts"
                    placeholder={pickedAccounts.length ? '' : 'All accounts'}
                    helperText={
                      pickedAccounts.length
                        ? `${pickedAccounts.length} account${pickedAccounts.length === 1 ? '' : 's'}`
                        : 'Leave empty for every account you can access'
                    }
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From"
                  value={from}
                  onChange={setFrom}
                  format="YYYY-MM-DD"
                  disabled={loading}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To"
                  value={to}
                  onChange={setTo}
                  format="YYYY-MM-DD"
                  disabled={loading}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Divider />

          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={run}
              disabled={!canRun}
            >
              {loading ? 'Running…' : 'Run report'}
            </Button>
            {checks.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {[
                  scope.auditRunIds.length
                    ? `${scope.auditRunIds.join(', ')} · ${scope.checkKeys.length} automated`
                    : null,
                  scope.manualKeys.length ? `${scope.manualKeys.length} manual` : null,
                ].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      </Grow>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Report failed</AlertTitle>
          {error}
        </Alert>
      )}


      {loading && !result && (
        <Paper elevation={0} sx={{ ...panelSx, p: 2, mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Querying {checks.length} check{checks.length === 1 ? '' : 's'} &middot;{' '}
              {from.format('YYYY-MM-DD')} to {to.format('YYYY-MM-DD')}…
            </Typography>
          </Stack>
        </Paper>
      )}


      {result && (
        <>
          {result.stoppedEarly && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Stopped early</AlertTitle>
              This query needed more than {MAX_PAGES} pages, so it shows the first{' '}
              {result.rowCount.toLocaleString()} rows. Narrow the accounts, dates or
              checks to see the rest.
            </Alert>
          )}

          {result.truncated && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Partial results</AlertTitle>
              This report hit the {(result.maxBytes / 1e6).toFixed(0)} MB response limit, so it
              shows {rows.length.toLocaleString()} rows out of a larger set. Narrow the
              accounts, dates or checks to see everything.
            </Alert>
          )}

            <Paper elevation={0} sx={{ ...panelSx, p: 2, mb: 3 }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Stat label="Rows" value={result.rowCount.toLocaleString()} />
              <Stat label="Facts read" value={result.records.toLocaleString()} />
              {result.manualCount > 0 && (
                <Stat label="Attestations" value={result.manualCount.toLocaleString()} />
              )}
              <Stat label="Accounts" value={result.accounts.toLocaleString()} />
              <Stat label="Suppressed" value={result.suppressedCount.toLocaleString()} />
              <Stat label="Dates" value={result.from === result.to ? result.from : `${result.from} → ${result.to}`} />
              {result.loadingMore && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    loading page {result.pages + 1} &middot; {result.rowCount.toLocaleString()} rows so far
                  </Typography>
                </Stack>
              )}
              {/* Nothing here when we stopped at MAX_PAGES -- that case has its own
                  warning banner, and a "complete" tick beside it would contradict it. */}
              {!result.loadingMore && !result.stoppedEarly && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                  <Typography variant="caption" color="text.secondary">
                    all rows loaded{result.pages > 1 ? ` · ${result.pages} pages` : ''}
                  </Typography>
                </Stack>
              )}

            </Stack>
          </Paper>

          <ResultsTable
            rows={rows}
            filename={`fleet-report-${result.from}.csv`}
          />
        </>
      )}
    </Container>
  );
}
